// Pure, testable auth/redirect helpers for the app router.
//
// Kept in their own module (rather than inline in index.tsx) so they can be
// unit-tested without importing the whole page/AppShell tree. Both encode the
// behaviour of the HTTP 414 redirect-loop fix — see router/auth.test.ts.

/**
 * Decide whether a build should use local dev-auth instead of the RBAC login
 * flow. `devAuth` (VITE_DEV_AUTH, baked into the dev image by dev-up.sh) is what
 * marks a non-localhost build — e.g. a Codespace on *.app.github.dev — as local.
 * `forceRbac` (VITE_FORCE_RBAC) always wins, so production can opt back in.
 */
export function computeIsLocal(opts: {
  forceRbac: boolean;
  devAuth: boolean;
  hostname: string;
}): boolean {
  return (
    !opts.forceRbac &&
    (opts.devAuth ||
      opts.hostname === 'localhost' ||
      opts.hostname === '127.0.0.1')
  );
}

/**
 * Build the RBAC login redirect URL, preserving where the user was headed.
 *
 * Returns null when `rbacUiUrl` is unconfigured: redirecting to an empty base
 * navigates back to the same page with an ever-growing ?returnTo, looping until
 * the URL exceeds the server limit (HTTP 414). Callers MUST refuse to redirect
 * on null rather than loop.
 */
export function buildRbacRedirectUrl(
  rbacUiUrl: string,
  currentHref: string,
): string | null {
  if (!rbacUiUrl) return null;
  return `${rbacUiUrl}?returnTo=${encodeURIComponent(currentHref)}`;
}
